import type { ComponentPropsWithRef, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

import { cn } from '@/shared/lib/cn'

type Variant = 'primary' | 'secondary' | 'quiet' | 'danger'
type Size = 'sm' | 'md'

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-bordeaux-500 text-white hover:bg-bordeaux-400 active:bg-bordeaux-600 border-transparent',
  secondary:
    'bg-transparent text-linen-200 border-[var(--hairline-strong)] hover:border-thread-400 hover:text-linen-100',
  quiet: 'bg-transparent text-thread-400 border-transparent hover:bg-ink-700 hover:text-linen-200',
  danger:
    'bg-transparent text-ruggine-500 border-ruggine-500 hover:bg-ruggine-500 hover:text-white',
}

const sizeClasses: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-meta gap-1.5',
  md: 'h-9 px-3.5 text-base gap-2',
}

function controlClasses(variant: Variant, size: Size, className?: string): string {
  return cn(
    'inline-flex items-center justify-center rounded-md border font-medium whitespace-nowrap',
    'transition-colors duration-[var(--dur-fast)] ease-thread',
    'disabled:pointer-events-none disabled:opacity-40',
    variantClasses[variant],
    sizeClasses[size],
    className,
  )
}

/** `ComponentPropsWithRef` perché in React 19 la ref è una prop come le altre. */
export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: Variant
  size?: Size
  /** Sostituisce il contenuto con un'etichetta di attesa e blocca il pulsante. */
  pending?: boolean
  pendingLabel?: string
  children: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  pending = false,
  pendingLabel = 'Attendi…',
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled ?? pending}
      aria-busy={pending || undefined}
      className={controlClasses(variant, size, className)}
      {...rest}
    >
      {pending ? pendingLabel : children}
    </button>
  )
}

export interface LinkButtonProps extends LinkProps {
  variant?: Variant
  size?: Size
  children: ReactNode
}

/**
 * Un collegamento con l'aspetto di un pulsante.
 *
 * Chi naviga deve poter aprire in una scheda nuova, copiare l'indirizzo,
 * tornare indietro: un `button` con `navigate()` toglie tutte e tre le cose.
 * Se l'azione porta altrove, è un link.
 */
export function LinkButton({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link className={controlClasses(variant, size, className)} {...rest}>
      {children}
    </Link>
  )
}
